import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useHub, usePrivate } from '../context';
import { loadQuests, loadVendors, loadXur, questDone, questFraction, saleCount, XUR, xurWindow, type Vendor } from '../character';
import { useSession } from '../../lib/session';
import { VendorShelves } from './CharacterTab';
import Landing from '../Landing';
import { distortionAt, loadWeekly, minutesToNextDistortion, ROTATION_ANCHOR, ROTATIONS, type Operation, type WeeklyTrack } from '../operations';
import { countUnder, RecordRow } from '../records';
import { inner, recordsUnder } from '../triumphs';
import { Art, Asking, Bar, Card, Draw, Heading, ItemTile, ObjectiveStrip, Pic, SectionHead, SideLabel, Split } from '../ui';

/** Port of the app's Overview: Operations (what is on now), the Seasonal Hub and Rotations. */
export default function OperationsTab({ sub }: { sub: string }) {
  if (sub === 'Rotations') return <RotationsPage />;
  if (sub === 'Seasonal Hub') return <SeasonalHub />;
  if (sub === 'Xur') return <XurPage />;
  return <OperationsPage />;
}

/** The front page. Signed out with nobody looked up, the parts about a Guardian give way to the sign-in card. */
function OperationsPage() {
  const h = useHub();
  const session = useSession();
  const hub = h.hub;
  return (
    <>
      <h1 className="g-sr">D2 Companion: what is on in Destiny 2 right now</h1>
      {!h.player && session.status === 'signedOut' && <Landing />}
      <div className="g-wrap">
        <DistortionsCard />
        <D2InfoCard />
        <ItemsCard />
      </div>
      <XurCard />
      {h.ops.length > 0 && (
        <div className="g-pair">
          <Hero ops={h.ops} />
          {hub?.hasRecords ? <Suggestions /> : !hub ? <SuggestSignIn /> : <Hero ops={[...h.ops].reverse()} />}
        </div>
      )}
      {pairs(h.cards).map((pair, i) => (
        <div className="g-pair" key={i}>
          {pair.map((c) => <Hero key={c.kind} ops={c.pages} />)}
        </div>
      ))}
      <SeasonStrip />
      {h.ops.length === 0 && hub && <Characters />}
    </>
  );
}

const pairs = <T,>(list: T[]) => list.reduce<T[][]>((all, x, i) => (i % 2 ? all[all.length - 1].push(x) : all.push([x]), all), []);

// ---------------- the big artwork cards ----------------

/**
 * A large card that pages through activities: the artwork filling it, the name and what it gives over the picture,
 * dashes underneath to pick a page. Pages cross-fade; it moves on by itself every 9 seconds unless the pointer is on it.
 */
export function Hero({ ops }: { ops: Operation[] }) {
  const [index, setIndex] = useState(0);
  const hover = useRef(false);
  useEffect(() => {
    if (ops.length < 2) return;
    const t = setInterval(() => { if (!hover.current) setIndex((i) => (i + 1) % ops.length); }, 9000);
    return () => clearInterval(t);
  }, [ops.length]);
  return (
    <div>
      <div className="g-hero" onPointerEnter={() => (hover.current = true)} onPointerLeave={() => (hover.current = false)}>
        {ops.map((op, i) => (
          <div key={op.name + i} className={`g-hero-page ${i === index ? 'on' : ''}`} aria-hidden={i !== index}>
            <Pic src={op.image} className="g-hero-art" />
            <div className="g-hero-shade" />
            {op.icon && <Pic src={op.icon} className="g-hero-mark" />}
            <div className="g-hero-text">
              <div className="g-hero-kind">{op.featured && op.facts.length > 0 ? `${op.kind}  |  featured` : op.kind}</div>
              <div className="g-hero-name">{op.name}</div>
              {op.rewards.length > 0 && (
                <div className="g-hero-rewards">
                  {op.rewards.slice(0, 7).map((r, k) => <span key={`${r.name}${k}`} className="g-hero-reward" title={r.name}><Pic src={r.icon} /></span>)}
                </div>
              )}
              <div className="g-hero-facts">
                {op.modifiers.length > 0 && (
                  <span className="g-hero-mods">
                    {op.modifiers.slice(0, 9).map((m, k) => (
                      <span key={`${m.name}${k}`} title={m.desc ? `${m.name}\n${m.desc}` : m.name}><Pic src={m.icon} width={26} height={26} alt={m.name} /></span>
                    ))}
                  </span>
                )}
                {op.facts.map((f) => <span key={f}>{f}</span>)}
                {op.ends && <span>{changesIn(op.ends)}</span>}
              </div>
            </div>
          </div>
        ))}
      </div>
      {ops.length > 1 && (
        <div className="g-pager">
          {ops.map((op, i) => (
            <button key={i} type="button" title={op.name} className={i === index ? 'on' : ''} style={{ width: ops.length > 8 ? 12 : 28 }} onClick={() => setIndex(i)} aria-label={op.name} />
          ))}
        </div>
      )}
    </div>
  );
}

function changesIn(end: Date) {
  const hours = (end.getTime() - Date.now()) / 3_600_000;
  return hours > 48 ? `Changes in ${Math.floor(hours / 24)} days` : hours > 1 ? `Changes in ${Math.round(hours)} hours` : 'Changes soon';
}

// ---------------- the card beside the activities: what is worth chasing next ----------------

/** In the place of Suggested for you, signed out: what it would show. */
function SuggestSignIn() {
  const h = useHub();
  const session = useSession();
  return (
    <div className="g-signin g-card">
      <div className="g-signin-icon"><Art d={Draw.Target} size={22} /></div>
      <h3>{session.status === 'loading' || h.player ? 'Suggested for you' : 'Sign in with Bungie to see this'}</h3>
      <p className="g-text2">Suggested for you: the seals, triumphs and catalysts you are closest to finishing, from your own Guardian's records.</p>
      {!h.player && session.status === 'signedOut' && <div className="g-landing-row"><button type="button" className="btn primary" onClick={session.signIn}>Sign in with Bungie</button></div>}
    </div>
  );
}

function Suggestions() {
  const h = useHub();
  const nav = useNavigate();
  const defs = h.defs;
  const hub = h.hub!;
  const cid = h.char?.id;
  const live = (r: number) => hub.record(r, cid);
  const rows: React.ReactNode[] = [];
  if (defs) {
    const seals = (defs.nodes.get(defs.roots.seals)?.nodes ?? [])
      .filter((s) => defs.nodes.has(s) && !live(defs.nodes.get(s)!.seal)?.done)
      .map((s) => ({ s, c: countUnder(defs, hub, cid, s) }))
      .filter((x) => x.c.total > 0 && x.c.done > 0)
      .sort((a, b) => b.c.done / b.c.total - a.c.done / a.c.total).slice(0, 4);
    for (const { s, c } of seals) {
      const node = defs.nodes.get(s)!;
      const title = defs.records.get(node.seal)?.title || node.name;
      rows.push(<SuggestRow key={`s${s}`} icon={node.icon} name={title} kind="Title" fraction={c.done / c.total} count={`${c.done} / ${c.total}`} tone="var(--accent-text)" onClick={() => nav('/guardian/triumphs')} />);
    }
    // weapon patterns and exotic catalysts under way: the three of each closest to done
    for (const [root, kind] of [[defs.more.crafting, 'Weapon Pattern'], [defs.more.catalysts, 'Exotic Catalyst']] as const) {
      const list = [...recordsUnder(defs, root)].map((r) => ({ r, l: live(r) }))
        .filter((x) => x.l && !x.l.done && !x.l.hidden && x.l.fraction > 0 && defs.records.has(x.r))
        .sort((a, b) => b.l!.fraction - a.l!.fraction).slice(0, 3);
      for (const { r, l } of list) {
        const def = defs.records.get(r)!;
        const last = l!.objectives[l!.objectives.length - 1];
        rows.push(<SuggestRow key={`c${r}`} icon={def.icon} name={def.name} kind={kind} fraction={l!.fraction}
          count={last && last.goal > 0 ? `${last.progress.toLocaleString()} / ${last.goal.toLocaleString()}` : `${Math.round(l!.fraction * 100)} %`} tone="#E6CEAE" onClick={() => nav('/guardian/collections/patterns-and-catalysts')} />);
      }
    }
    const active = [...recordsUnder(defs, defs.roots.active)].map((r) => ({ r, l: live(r) }))
      .filter((x) => x.l && !x.l.done && !x.l.hidden && !x.l.secret && !x.l.locked && x.l.fraction > 0)
      .sort((a, b) => b.l!.fraction - a.l!.fraction).slice(0, 8);
    for (const { r, l } of active) {
      const def = defs.records.get(r);
      rows.push(<SuggestRow key={`t${r}`} icon={def?.icon} name={def?.name ?? 'Triumph'} kind={def && def.score > 0 ? `Triumph  +${def.score}` : 'Triumph'} fraction={l!.fraction} count={`${Math.round(l!.fraction * 100)} %`} tone="var(--info)" />);
    }
  }
  return (
    <div>
      <div className="g-suggest">
        <div className="g-suggest-head">
          <div className="g-suggest-kicker">Suggested for you</div>
          <div className="g-suggest-title">Closest to done</div>
        </div>
        <div className="g-suggest-list">{defs ? rows : <Asking text="Reading triumphs from Bungie's database (once per Destiny update)..." />}</div>
      </div>
      <div style={{ height: 27 }} />
    </div>
  );
}

function SuggestRow({ icon, name, kind, fraction, count, tone, onClick }: { icon?: string; name: string; kind: string; fraction: number; count: string; tone: string; onClick?: () => void }) {
  const inside = (
    <>
      <Pic src={icon} className="g-suggest-icon" />
      <div className="g-suggest-mid">
        <div className="g-suggest-top"><b>{name}</b><span>{kind}</span></div>
        <Bar fraction={fraction} color={tone} height={3} />
      </div>
      <span className="g-suggest-count">{count}</span>
    </>
  );
  return onClick ? <button type="button" className="g-suggest-row" onClick={onClick}>{inside}</button> : <div className="g-suggest-row">{inside}</div>;
}

// ---------------- the front page's small cards ----------------

/** Which destination is distorted this hour, and the next three. */
function DistortionsCard() {
  const [, tick] = useState(0);
  useEffect(() => { const t = setInterval(() => tick((n) => n + 1), 30_000); return () => clearInterval(t); }, []);
  const now = distortionAt(0);
  return (
    <Card className="g-distort" style={{ padding: 16 }}>
      <div title="Each hour a different destination is distorted in the game. Bungie's data does not say which, so this follows the rotation as it was seen.">
        <div className="g-caps-label">DISTORTIONS</div>
        <div className="g-distort-now">{now.name}</div>
        <div className="g-text2 small">Distorted now. Next in {minutesToNextDistortion()} min.</div>
      </div>
      <div className="g-vrule" />
      {[1, 2, 3].map((n) => {
        const d = distortionAt(n);
        return (
          <div key={n} className="g-distort-next">
            <div className="g-dim small">{d.starts.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
            <b>{d.name}</b>
          </div>
        );
      })}
    </Card>
  );
}

/** Beside the Distortions: the way into D2 Info, the item look-up (also a tile on the left). */
function D2InfoCard() {
  const nav = useNavigate();
  return (
    <button type="button" className="g-card g-holdings g-d2infocard" title="Open D2 Info's Item Search" onClick={() => nav('/guardian/operations/d2-info?section=search')}>
      <Art d={Draw.Book} size={34} />
      <div>
        <div className="g-caps-label">D 2   I N F O</div>
        <div className="g-distort-now">Item look-up</div>
        <div className="g-text2 small">Look up any weapon or armour, with god rolls.</div>
      </div>
    </button>
  );
}

/** Beside Xûr: the way into the Item Manager (also a tile on the left). */
function ItemsCard() {
  const nav = useNavigate();
  return (
    <button type="button" className="g-card g-holdings g-d2infocard" title="Open the Item Manager" onClick={() => nav('/guardian/operations/item-manager')}>
      <Art d={Draw.VaultDoor} size={34} />
      <div>
        <div className="g-caps-label">I T E M S</div>
        <div className="g-distort-now">Item Manager</div>
        <div className="g-text2 small">Your characters and vault side by side. Drag to move or equip.</div>
      </div>
    </button>
  );
}

// ---------------- Xûr ----------------

// asked once a visit. Signed in (your own Guardian shown): your vendor list, where his doorway items ("More Strange
// Offers", "Strange Gear Offers") open into their shelves and what you own is marked; otherwise Bungie's public list,
// the same for everyone but without those shelves (Bungie only gives them to a signed-in player)
const xurJobs = new Map<string, Promise<{ xur?: Vendor; full: boolean }>>();
function useXur() {
  const h = useHub();
  const mine = h.signedIn && h.own && !!h.char;
  const key = mine ? `me:${h.char!.id}` : 'public';
  const [state, setState] = useState<{ xur?: Vendor; full: boolean; done: boolean; error?: string }>({ full: false, done: false });
  useEffect(() => {
    let live = true;
    let job = xurJobs.get(key);
    if (!job) {
      const pub = () => loadXur().then((xur) => ({ xur, full: false }));
      job = mine
        ? h.getToken().then((t) => (t ? loadVendors(h.player!, t, h.char!.id) : Promise.reject(new Error('signed out'))))
          .then((all) => { const xur = all.find((v) => v.hash === XUR); return xur ? { xur, full: true } : pub(); })
          .catch(pub)
        : pub();
      xurJobs.set(key, job);
    }
    setState((s) => ({ ...s, done: false }));
    job.then((r) => live && setState({ ...r, done: true }))
      .catch((e) => { xurJobs.delete(key); if (live) setState({ full: false, done: true, error: e instanceof Error ? e.message : String(e) }); });
    return () => { live = false; };
  }, [key]);   // eslint-disable-line react-hooks/exhaustive-deps
  return state;
}
const untilText = (d?: Date) => {
  if (!d || d.getTime() <= Date.now()) return '';
  const ms = d.getTime() - Date.now(), days = Math.floor(ms / 86_400_000), hours = Math.floor((ms % 86_400_000) / 3_600_000);
  return days > 0 ? `${days} d ${hours} h` : `${hours} h ${Math.floor((ms % 3_600_000) / 60_000)} min`;
};

const whenText = (d: Date) => `${d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })}, ${d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}`;

// Bungie's backdrops for Xûr and Starhorse in Eternity (the vendor definitions' location art): laid side by side
const XUR_ART = '/img/destiny_content/factions/vendors/eternity-xur.jpg';
const STARHORSE_ART = '/img/destiny_content/factions/vendors/eternity-starhorse.jpg';

/**
 * Under the top cards, like his screen in the game: Xûr and Starhorse, whether he is here (a live dot) and for how
 * long, and his shelves as icons. Away (Tuesday to Friday) it says when he is back. A click opens what he sells.
 */
function XurCard() {
  const nav = useNavigate();
  const { xur, done, error } = useXur();
  const win = xurWindow();
  const here = win.here && !!xur;
  const status = !win.here ? 'Away' : !done ? 'Asking Bungie...' : error ? 'Bungie did not answer' : xur ? 'Here now' : 'Away';
  const line = !win.here ? `Back on Friday, in ${untilText(win.arrives)}`
    : xur ? `Leaves at the weekly reset, in ${untilText(win.leaves)}` : '';
  const shelves = here ? xur!.categories.map((c) => ({ ...c, sales: c.sales.filter((s) => !s.door) })).filter((c) => c.sales.length > 1).slice(0, 2) : [];   // not a shelf of one (his "Xûr" info item)
  return (
    <button type="button" className={`g-xurtile ${here ? 'here' : 'away'}`} title="Open Xûr: what he sells this week" onClick={() => nav('/guardian/operations/xur')}>
      <Pic src={STARHORSE_ART} className="g-xurtile-horse" />
      <Pic src={XUR_ART} className="g-xurtile-xur" />
      <div className="g-xurtile-shade" />
      <div className="g-xurtile-text">
        <div className="g-xurtile-kind">The Nine</div>
        <div className="g-xurtile-name">Xûr</div>
        <div className="g-xurtile-status">{here && <i className="dot" />}<b>{status}</b>{line && <span>{line}</span>}</div>
        {shelves.map((c) => (
          <div key={c.name} className="g-xurtile-shelf">
            <div className="g-xurtile-cat">{c.name}</div>
            <div className="g-xurtile-items">
              {c.sales.slice(0, 8).map((s, k) => <span key={`${s.hash}${k}`} className={`g-xurtile-item ${s.owned ? 'owned' : ''}`} title={s.typeName ? `${s.name}\n${s.typeName}` : s.name}><Pic src={s.icon} /></span>)}
            </div>
          </div>
        ))}
      </div>
    </button>
  );
}

/** Xûr's page: his stock this week, the same for everyone (Bungie's public vendor list, no sign-in needed). */
function XurPage() {
  const { xur, full, done, error } = useXur();
  const session = useSession();
  const h = useHub();
  const win = xurWindow();
  if (!win.here) return (
    <div className="g-stack">
      <h1 className="g-title light">Xûr</h1>
      <p className="g-text2">Xûr is away. He comes back on Friday at 17:00 UTC ({whenText(win.arrives!)}, in {untilText(win.arrives)}) and stays until the weekly reset on Tuesday.</p>
    </div>
  );
  if (!done) return <Asking text="Asking Bungie what Xûr is selling..." />;
  if (error) return <p className="g-text2">Bungie did not answer: {error}</p>;
  if (!xur) return (
    <div className="g-stack">
      <h1 className="g-title light">Xûr</h1>
      <p className="g-text2">Bungie lists no stock for Xûr right now. Look again after the weekly reset.</p>
    </div>
  );
  // doorway items that did not open (signed out): not tiles that do nothing, but one note saying how to see them
  const doors = xur.categories.flatMap((c) => c.sales).filter((s) => s.door);
  const shelves = { ...xur, categories: xur.categories.map((c) => ({ ...c, sales: c.sales.filter((s) => !s.door) })).filter((c) => c.sales.length) };
  const facts = [`${saleCount(shelves)} items on sale`];
  facts.push(`Leaves in ${untilText(win.leaves)} (${whenText(win.leaves!)})`);
  return (
    <div className="g-stack">
      <h1 className="g-title light">Xûr this week</h1>
      <p className="g-text2">{full ? `What Xûr sells your ${h.char?.cls ?? 'Guardian'} this week, his other shelves opened and what you already own marked in gold. Weapons and armour open in D2 Info.`
        : 'What Xûr sells this week, the same for everyone. Weapons and armour open in D2 Info.'}</p>
      {doors.length > 0 && (
        <div className="g-card g-xurdoors">
          <div>
            <b>Xûr has more shelves: {doors.map((d) => d.name).join(' and ')}</b>
            <p className="g-text2 small">Bungie only shows what is on them to a signed-in player. Sign in with Bungie to see them here, with what you already own marked.</p>
          </div>
          {!h.signedIn && <button type="button" className="btn primary" onClick={session.signIn}>Sign in with Bungie</button>}
        </div>
      )}
      <VendorShelves v={shelves} facts={facts} />
    </div>
  );
}

/** The characters as cards (shown when Bungie reports nothing on this week). */
function Characters() {
  const hub = useHub().hub!;
  const total = Math.max(1, hub.chars.reduce((s, c) => s + c.minutes, 0));
  return (
    <>
      <Heading>Characters</Heading>
      <div className="g-wrap">
        {hub.chars.map((c) => (
          <Card key={c.id} className="g-charcard">
            <Pic src={c.emblem} width={56} height={56} />
            <div>
              <div><b className="big">{c.cls}</b> <b className="big gold">{c.light}</b></div>
              <div className="g-text2 small">{c.race}  |  {Math.floor(c.minutes / 60).toLocaleString()} hours ({Math.round((c.minutes * 100) / total)} %)</div>
              <div className="g-dim small">Last played {c.lastPlayed.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}</div>
            </div>
          </Card>
        ))}
      </div>
    </>
  );
}

// ---------------- the season strip ----------------

/** The season: name and a huge faint rank on the left, the reward track on the right. */
function SeasonStrip() {
  const h = useHub();
  const s = h.season;
  if (!h.hub) return null;   // signed out with nobody looked up: no season rank to show
  if (s === undefined) return <Asking text="Asking Bungie for the season..." />;
  if (!s || !h.char) return null;
  const p = h.hub!.progress(h.char.id, s.rewardProgression) ?? { level: 0, into: 0, size: 0 };
  const levels = [...new Set(s.rewards.map((r) => r.level))].sort((a, b) => a - b).slice(0, 10);
  return (
    <div className="g-season">
      <div className="g-season-left">
        <div className="g-season-ghost">{p.level}</div>
        <div className="g-season-words">
          <div className="g-season-name">{s.name || s.passName}</div>
          <p className="g-season-desc">{s.desc || s.passName}</p>
        </div>
        <div className="g-season-foot">
          <div className="g-season-labels"><span>S E A S O N   R A N K   {p.level}</span>{p.size > 0 && <span>{p.into.toLocaleString()} / {p.size.toLocaleString()}</span>}</div>
          <Bar fraction={p.size > 0 ? p.into / p.size : 0} color="#f2f3f7" height={3} />
        </div>
      </div>
      {levels.length > 0 && (
        <div className="g-season-track" style={{ gridTemplateColumns: `repeat(${levels.length}, minmax(0, 1fr))` }}>
          {levels.map((l, i) => <div key={`n${l}`} className={`g-season-level ${l <= p.level ? 'on' : ''}`} style={{ gridColumn: i + 1 }}>{l}</div>)}
          <div className="g-season-dash" style={{ gridColumn: `1 / span ${levels.length}` }} />
          <div className="g-season-band" style={{ gridColumn: `1 / span ${levels.length}` }} />
          {levels.map((l, i) => <SeasonCell key={`f${l}`} column={i + 1} reward={s.rewards.find((r) => r.level === l && !r.premium)} level={l} reached={l <= p.level} />)}
          {levels.map((l, i) => <SeasonCell key={`p${l}`} column={i + 1} reward={s.rewards.find((r) => r.level === l && r.premium)} level={l} reached={l <= p.level} paid />)}
        </div>
      )}
    </div>
  );
}

function SeasonCell({ reward, level, reached, paid, column }: { reward?: { hash: number; name: string; icon: string }; level: number; reached: boolean; paid?: boolean; column: number }) {
  const nav = useNavigate();
  if (!reward) return <div className={`g-season-cell ${paid ? 'paid' : ''}`} style={{ gridColumn: column }} />;
  return (
    <div className={`g-season-cell ${paid ? 'paid' : ''}`} style={{ gridColumn: column }}>
      <button type="button" className={`g-season-icon ${paid ? 'paid' : ''}`} onClick={() => nav(`/guardian/operations/d2-info?item=${reward.hash}`)}
        title={`${reward.name}\nRank ${level}${paid ? '  |  paid track' : '  |  free track'}${reached ? '  |  reached' : ''}\n\nClick to open it in D2 Info`}><Pic src={reward.icon} /></button>
    </div>
  );
}

// ---------------- Seasonal Hub ----------------

function SeasonalHub() {
  const h = useHub();
  return (
    <>
      <SeasonStrip />
      <ActiveOrders />
      <WeeklyRewards />
      {h.hub!.hasRecords && <SeasonChallenges />}
    </>
  );
}

/** The orders the character is carrying (private: only with the sign-in). */
function ActiveOrders() {
  const h = useHub();
  const { data: quests, error } = usePrivate(h.char ? `quests:${h.char.id}` : undefined, (t) => loadQuests(h.player!, t, h.char!.id));
  const orders = (quests ?? []).filter((q) => q.order).sort((a, b) => Number(questDone(b)) - Number(questDone(a)) || questFraction(b) - questFraction(a));
  return (
    <div className="g-block">
      <SectionHead title="Active Orders" count={quests ? `${orders.filter(questDone).length} / ${orders.length} done` : undefined} size={14.5} />
      {!h.own ? <p className="g-dim">Sign in with Bungie (top right) to see the orders your character is carrying.</p>
        : !quests ? <Asking text={error ?? 'Asking Bungie for your orders...'} />
          : orders.length === 0 ? <p className="g-text2">Your character is carrying no orders.</p>
            : (
              <div className="g-wrap">
                {orders.map((o) => (
                  <Card key={o.hash} className="g-order" done={questDone(o)}>
                    <span className={`g-tile ${questDone(o) ? 'gold' : ''}`} style={{ width: 44, height: 44 }}><Pic src={o.icon} /></span>
                    <div className="g-order-main" title={`${o.name}\n${o.desc}`}>
                      <b className={questDone(o) ? 'good' : ''}>{o.name}</b>
                      {o.typeName && <div className="g-dim small">{o.typeName}</div>}
                      {o.desc && <p className="g-text2 small clamp3">{o.desc}</p>}
                      {o.objectives.map((ob, i) => <ObjectiveStrip key={i} text={ob.text} progress={ob.progress} goal={ob.goal > 0 ? ob.goal : 1} done={ob.done} />)}
                    </div>
                  </Card>
                ))}
              </div>
            )}
    </div>
  );
}

/** The Seasonal Hub's reward track: a tile per rank; earned ones lit, claimed ones ticked. */
function WeeklyRewards() {
  const h = useHub();
  const nav = useNavigate();
  const [w, setW] = useState<WeeklyTrack | null>();
  useEffect(() => {
    if (!h.hub || !h.char || !h.defs) return;
    let live = true;
    loadWeekly(h.hub, h.char.id, h.defs.seasonHub.card).then((t) => live && setW(t)).catch(() => live && setW(null));
    return () => { live = false; };
  }, [h.hub, h.char?.id, h.defs]);   // eslint-disable-line react-hooks/exhaustive-deps
  if (!w?.rewards.length) return null;
  const done = w.rank >= w.ranks && w.ranks > 0;
  return (
    <div className="g-block">
      <SectionHead title="Weekly Rewards" size={14.5}
        count={done ? `Rank ${w.rank} / ${w.ranks}: all earned` : `Rank ${w.rank}${w.ranks > 0 ? ' / ' + w.ranks : ''}   |   ${w.into.toLocaleString()} / ${w.size.toLocaleString()} to the next`} />
      <div className="g-wrap tight">
        {w.rewards.map((r) => (
          <div key={r.rank + ':' + r.item} className="g-weekly">
            <span className={r.earned ? '' : 'g-dim'}>{r.rank}</span>
            <ItemTile icon={r.icon} name={r.name} corner={r.quantity > 1 ? r.quantity.toLocaleString() : ''} size={56} gold={r.earned && !r.claimed} dim={!r.earned}
              more={`Rank ${r.rank}  |  ${r.claimed ? 'Claimed' : r.earned ? 'Earned: claim it in the game' : 'Not earned yet'}\n\nClick to open it in D2 Info`}
              onClick={() => nav(`/guardian/operations/d2-info?item=${r.item}`)} />
            <span className={r.claimed ? 'good' : 'gold'}>{r.claimed ? '✓' : r.earned ? 'claim in game' : ''}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** The Seasonal Hub's challenges: one column per group (daily, weekly...), each challenge as a card. */
function SeasonChallenges() {
  const h = useHub();
  const defs = h.defs;
  if (!defs) return <Asking text="Reading triumphs from Bungie's database (once per Destiny update)..." />;
  const live = (r: number) => h.hub!.record(r, h.char?.id);
  const root = inner(defs, defs.seasonHub.challenges);
  const node = defs.nodes.get(root);
  if (!node) return null;
  const groups: { name: string; records: number[] }[] = [];
  const gather = (hash: number, name: string) => {
    const n = defs.nodes.get(hash);
    if (!n) return;
    const shown = n.records.filter((r) => defs.records.has(r) && !live(r)?.hidden);
    if (shown.length) groups.push({ name, records: shown });
    n.nodes.forEach((c) => gather(c, defs.nodes.get(c)?.name ?? name));
  };
  gather(root, node.name);
  // the game leaves these groups unnamed: the ones holding a single challenge are the daily ones, the rest weekly
  const daily = groups.filter((g) => !g.name && g.records.length === 1).flatMap((g) => g.records);
  const weekly = groups.filter((g) => !g.name && g.records.length !== 1).flatMap((g) => g.records);
  const named = groups.filter((g) => g.name);
  const all = [...(daily.length ? [{ name: 'Daily Challenges', records: daily }] : []), ...(weekly.length ? [{ name: 'Weekly Challenges', records: weekly }] : []), ...named];
  const title = defs.nodes.get(defs.seasonHub.seal);
  if (title?.records.length) all.push({ name: `Seasonal Title: ${title.name}`, records: title.records.filter((r) => defs.records.has(r) && !live(r)?.hidden) });
  if (!all.length) return null;
  const n = Math.min(all.length, 3);
  const cols: (typeof all)[] = Array.from({ length: n }, () => []);
  all.forEach((g, i) => cols[i % n].push(g));
  return (
    <div className="g-cols" style={{ gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))` }}>
      {cols.map((col, i) => (
        <div key={i} className="g-stack">
          {col.map((g) => {
            const done = g.records.filter((r) => live(r)?.done).length;
            return (
              <div key={g.name} className="g-stack">
                <SectionHead title={g.name} count={`${done} / ${g.records.length}`} size={14.5} />
                {[...g.records].sort((a, b) => Number(!!live(a)?.done) - Number(!!live(b)?.done)).map((r) => <RecordRow key={r} hash={r} />)}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// ---------------- Rotations ----------------

function RotationsPage() {
  const h = useHub();
  const days = Math.floor((Date.now() - ROTATION_ANCHOR) / 86_400_000);
  const weeks = Math.floor(days / 7);
  const blocks = ROTATIONS.map((r) => {
    const now = r.weekly ? weeks : days;
    const rows = [];
    for (let step = r.weekly ? -2 : 0; step <= (r.weekly ? 4 : 6); step++) {
      const when = new Date(ROTATION_ANCHOR + (now + step) * (r.weekly ? 7 : 1) * 86_400_000);
      const until = new Date(when.getTime() + (r.weekly ? 7 : 1) * 86_400_000);
      const entry = r.cycle[(((r.at + now + step) % r.cycle.length) + r.cycle.length) % r.cycle.length];
      rows.push(
        <div key={step} className={`g-rot ${step === 0 ? 'on' : ''}`}
          title={step === 0 ? `On now, until ${until.toLocaleString(undefined, { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })}` : `From ${when.toLocaleString(undefined, { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })}`}>
          <span className="when">{when.toLocaleDateString(undefined, { month: 'long', day: 'numeric' })}</span>
          <span className="what">{entry}</span>
        </div>,
      );
    }
    return (
      <div key={r.name} className="g-rotblock">
        <SideLabel text={r.name} />
        <div className="g-rotrows">{rows}</div>
      </div>
    );
  });
  return (
    <>
      <Split
        left={(
          <>
            <h1 className="g-title">Rotations</h1>
            <p className="g-text2 g-intro">Rituals that run on a rotation, use these projections to plan your endeavours. They are worked out from the order the game has followed so far; when Bungie changes a rotation, they will be off until the site is updated.</p>
          </>
        )}
        right={<div className="g-cols two"><div className="g-stack big">{blocks.filter((_, i) => i % 2 === 0)}</div><div className="g-stack big">{blocks.filter((_, i) => i % 2 === 1)}</div></div>}
      />
      {h.ops.length > 0 && <Heading style={{ marginTop: 30 }}>Featured this week, as Bungie reports it</Heading>}
      {pairs(h.ops).map((pair, i) => (
        <div className="g-pair" key={i}>{pair.map((op) => <Hero key={op.name} ops={[op]} />)}</div>
      ))}
    </>
  );
}
